"use client";

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ArticleCard } from "./ArticleCard";

interface Tag {
  id: number;
  name: string;
  slug: string;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  intro: string | null;
  content: string | null;
  header_image_url: string | null;
  view_count: number;
  created_at: string;
  tags: Tag[];
}

interface RelatedArticlesProps {
  currentArticleId: string;
  currentArticleTags: Tag[];
  limit?: number;
}

/**
 * RelatedArticles Component
 *
 * OPTIMIZATION STRATEGY:
 * 1. Client-side component (no SSR overhead)
 * 2. Fetches only when needed (lazy loading)
 * 3. Uses tag intersection for relevance scoring
 * 4. Limits to 3 articles by default (minimal data transfer)
 * 5. Caches in session storage (no repeated queries)
 */
export function RelatedArticles({
  currentArticleId,
  currentArticleTags,
  limit = 3
}: RelatedArticlesProps) {
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      // Check session cache first (cost optimization)
      const cacheKey = `related_${currentArticleId}`;
      const cached = sessionStorage.getItem(cacheKey);

      if (cached) {
        setRelatedArticles(JSON.parse(cached));
        setIsLoading(false);
        return;
      }

      if (currentArticleTags.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        // Strategy: Find articles that share at least one tag
        const tagIds = currentArticleTags.map(t => t.id);

        // Get article IDs that share tags
        const { data: sharedTagArticles, error: tagError } = await supabase
          .from('article_tags')
          .select('article_id, tag_id')
          .in('tag_id', tagIds)
          .neq('article_id', currentArticleId);

        if (tagError) throw tagError;

        if (!sharedTagArticles || sharedTagArticles.length === 0) {
          setIsLoading(false);
          return;
        }

        // Count shared tags per article (relevance score)
        const articleScores = new Map<string, number>();
        sharedTagArticles.forEach(({ article_id }) => {
          articleScores.set(article_id, (articleScores.get(article_id) || 0) + 1);
        });

        // Sort by relevance (most shared tags first)
        const sortedArticleIds = Array.from(articleScores.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, limit)
          .map(([id]) => id);

        // Fetch full article data for top matches
        const { data: articles, error: articlesError } = await supabase
          .from('articles')
          .select(`
            id,
            title,
            slug,
            intro,
            content,
            header_image_url,
            view_count,
            created_at,
            article_tags (
              tags (
                id,
                name,
                slug
              )
            )
          `)
          .in('id', sortedArticleIds)
          .eq('is_public', true);

        if (articlesError) throw articlesError;

        // Transform and maintain sort order
        const articlesMap = new Map(
          (articles || []).map((article: any) => [
            article.id,
            {
              ...article,
              tags: article.article_tags?.map((at: any) => at.tags).filter(Boolean) || []
            }
          ])
        );

        const sortedArticles = sortedArticleIds
          .map(id => articlesMap.get(id))
          .filter(Boolean) as Article[];

        setRelatedArticles(sortedArticles);

        // Cache for session (cost optimization)
        sessionStorage.setItem(cacheKey, JSON.stringify(sortedArticles));
      } catch (error) {
        console.error('Error fetching related articles:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [currentArticleId, currentArticleTags, limit]);

  if (isLoading) {
    return (
      <section className="py-16 bg-gradient-to-br from-orange-50/50 to-amber-50/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-right mb-12" dir="rtl">
            <h2 className="text-3xl md:text-4xl font-bold text-orange-800 mb-4">
              מאמרים קשורים
            </h2>
            <p className="text-gray-600 text-lg">
              גלו מאמרים נוספים שעשויים לעניין אתכם
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border border-orange-100 overflow-hidden">
                <div className="h-48 bg-orange-100 animate-pulse" />
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded animate-pulse mb-3" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (relatedArticles.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-br from-orange-50/50 to-amber-50/30">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-right mb-12" dir="rtl">
          <h2 className="text-3xl md:text-4xl font-bold text-orange-800 mb-4">
            מאמרים קשורים
          </h2>
          <p className="text-gray-600 text-lg">
            גלו מאמרים נוספים שעשויים לעניין אתכם
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {relatedArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}
